import React from "react";
import ReactDOM from "react-dom/client";
import { SchoolTile } from "@app/components/SchoolTile";
import { ActiveFilters, EVENT_SORT_DRAWER_OPEN, Filter } from "@app/components/SortDrawer";
import { DrawerWrapper } from "@app/components/pageCollections/DrawerWrapper";

export const EVENT_FILTER_UPDATE = "sortDrawer:filterUpdate";
export const EVENT_SCHOOL_COUNT_UPDATE = "schoolList:update";

export const INITIAL_ACTIVE_FILTERS: ActiveFilters[] = [
  { id: "state", options: [] },
  { id: "school_type", options: [] },
];

class SchoolList extends HTMLElement {
  $sortDrawerContainer!: HTMLDivElement;
  $filterButton!: HTMLButtonElement;
  $filterStateButton!: HTMLButtonElement;
  $filterTypeButton!: HTMLButtonElement;
  $searchInput!: HTMLButtonElement;
  $searchButton!: HTMLInputElement;
  $list!: HTMLElement;
  $groupTitleClone!: HTMLElement;
  $groupWrapperClone!: HTMLElement;

  filters: Filter[] = [];
  activeFilters: ActiveFilters[] = [...INITIAL_ACTIVE_FILTERS];

  constructor() {
    super();
    this.$sortDrawerContainer = this.querySelector(".sort-drawer-container")!;
    this.$filterButton = this.querySelector(".collections-filter-button")!;
    this.$filterStateButton = this.querySelector(".collections-filter-state-button")!;
    this.$filterTypeButton = this.querySelector(".collections-filter-type-button")!;
    this.$searchInput = this.querySelector(".collections-search-input")!;
    this.$searchButton = this.querySelector(".collections-search-button")!;
    this.$list = this.querySelector(".school-items")!;

    const title = this.querySelector(".school-items .group-title")!;
    const wrapper = this.querySelector(".school-items .group-wrapper")!;
    this.$groupTitleClone = title.cloneNode(true) as HTMLElement;
    this.$groupWrapperClone = wrapper.cloneNode() as HTMLElement;

    title.remove();
    wrapper.remove();
  }

  connectedCallback() {
    if (window.allCollections?.length) {
      this.$filterButton.addEventListener("click", () => {
        document.dispatchEvent(new CustomEvent(EVENT_SORT_DRAWER_OPEN));
      });

      this.$filterStateButton.addEventListener("click", () => {
        document.dispatchEvent(new CustomEvent(EVENT_SORT_DRAWER_OPEN, { detail: { accordionItem: "state" } }));
      });

      this.$filterTypeButton.addEventListener("click", () => {
        document.dispatchEvent(new CustomEvent(EVENT_SORT_DRAWER_OPEN, { detail: { accordionItem: "school_type" } }));
      });

      this.$searchButton.addEventListener("click", () => {
        this.handleSearch();
      });

      this.$searchInput.addEventListener("keyup", (event) => {
        if (event.key === "Enter") {
          this.handleSearch();
        }
      });

      this.filters = [
        { id: "state", name: "State", options: [] },
        { id: "school_type", name: "School Type", options: [] },
      ];

      window.allCollections.forEach((school) => {
        const state = school.metafields.state;
        const schoolType = school.metafields.school_type;

        const stateIndex = this.filters.findIndex((filter) => filter.id === "state");
        const schoolTypeIndex = this.filters.findIndex((filter) => filter.id === "school_type");

        const optionIndex = this.filters[stateIndex].options.findIndex((option) => option.id === state);
        if (optionIndex === -1) {
          this.filters[stateIndex].options.push({ id: state, name: state, value: state, resultsLength: 1 });
        } else {
          this.filters[stateIndex].options[optionIndex].resultsLength!++;
        }

        const optionIndex2 = this.filters[schoolTypeIndex].options.findIndex((option) => option.id === schoolType);
        if (optionIndex2 === -1) {
          this.filters[schoolTypeIndex].options.push({
            id: schoolType,
            name: schoolType,
            value: schoolType,
            resultsLength: 1,
          });
        } else {
          this.filters[schoolTypeIndex].options[optionIndex2].resultsLength!++;
        }
      });

      this.handleFilterChange();

      const updateFilters = (activeFilters: ActiveFilters[]) => {
        this.activeFilters = activeFilters;
        this.handleFilterChange();
        document.dispatchEvent(new CustomEvent(EVENT_FILTER_UPDATE, { detail: { activeFilters } }));
      };

      // mount sort drawer
      const root = ReactDOM.createRoot(this.$sortDrawerContainer);
      root.render(
        <DrawerWrapper
          initialTotalResults={window.allCollections.length}
          filters={this.filters}
          initialActiveFilters={this.activeFilters}
          onFilterUpdate={updateFilters}
        />,
      );
    }
  }

  getFilteredSchools(startingSchools?: any[]) {
    let schools = startingSchools || window.allCollections;

    this.activeFilters.forEach((filter) => {
      if (!filter.options.length) return;
      schools = schools.filter(
        (school) => school.metafields[filter.id] !== undefined && filter.options.includes(school.metafields[filter.id]),
      );
    });

    return schools;
  }

  getSearchedSchools() {
    const value = this.$searchInput.value.trim();
    let schools = window.allCollections;
    if (value) {
      schools = window.allCollections.filter((school) => school.title.toLowerCase().includes(value.toLowerCase()));
    }
    return schools;
  }

  handleFilterChange() {
    const schools = this.getFilteredSchools(this.getSearchedSchools());
    document.dispatchEvent(new CustomEvent(EVENT_SCHOOL_COUNT_UPDATE, { detail: { count: schools.length } }));
    this.renderSchoolList(schools);
  }

  handleSearch() {
    let schools = this.getSearchedSchools();

    if (schools.length) {
      schools = this.getFilteredSchools(schools);
    }

    document.dispatchEvent(new CustomEvent(EVENT_SCHOOL_COUNT_UPDATE, { detail: { count: schools.length } }));
    this.renderSchoolList(schools);
  }

  renderSchoolList(schools: any[]) {
    this.$list.innerHTML = "";

    if (schools?.length) {
      // Group schools alphabetically
      const data = schools.reduce((r, e) => {
        let group = e.title[0].toUpperCase();
        if (!r[group]) r[group] = { group, schools: [e] };
        else r[group].schools.push(e);
        return r;
      }, {});

      const groupedSchools = Object.values(data) as any[];

      groupedSchools.forEach((group) => {
        const $groupTitle = this.$groupTitleClone.cloneNode(true) as HTMLElement;
        $groupTitle.querySelector(":scope > div")!.innerHTML = group.group;
        this.$list.appendChild($groupTitle);
        const $groupWrapper = this.$groupWrapperClone.cloneNode() as HTMLElement;
        this.$list.appendChild($groupWrapper);

        group.schools.forEach((school: any) => {
          const $schoolItem = document.createElement("div");
          const root = ReactDOM.createRoot($schoolItem);
          root.render(
            <SchoolTile name={school.title} image={school.image?.src} href={`/collections/${school.handle}`} />,
          );
          $groupWrapper.appendChild($schoolItem);
        });
      });
    }
  }
}

customElements.define("school-list", SchoolList);
